import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import styles from './styles.module.scss'

import { getDocumentFileBlob, sendDocumentOutstandingReminder } from '../../../../api'
import useNotify from '../../../../hooks/useNotify'

import FileViewer from '../../../FileViewer'

const getBreakdownStatus = (item) => {
  const status = item.key
  switch (status) {
    case 'overdue':
      return 'overdue';
    case 'not_seen':
      return 'notSeen';
    case 'acknowledged':
      return 'acknowledged';
    case 'seen_not_acknowledged':
      return 'seen';
    default:
      return 'other'
  }
}

const formatDate = (date) => {
  if (!date || !moment(date).isValid()) return '-'
  return moment(date).format('MMM D, YYYY')
}

const Overview = ({ document, completion, companyId }) => {
  const { t } = useTranslation()
  const notify = useNotify()

  const [files, setFiles] = useState([])
  const [loadingFiles, setLoadingFiles] = useState(false)

  const isCompanyOwner = document.document_type.ownership === 'company'

  useEffect(() => {
    if (document.files.length) {
      loadFiles()
    }
  }, [document.files.length])

  const loadFiles = async () => {
    setLoadingFiles(true)
    const res = await Promise.all(document.files.map(file => getDocumentFileBlob(51, file.document_id, file.id).then(blob => {
      return {
        file: blob,
        name: file.name,
        mime_type: file.mime_type
      }
    })))
    setFiles(res)
    setLoadingFiles(false)
  }

  const renderAudienceLabel = (document) => {
    switch (document.assignment_type) {
      case 'company':
        return t('All employees')
      case 'place':
        return document.place.name
      case 'employee':
        return t('Selected employees')
      default:
        return ''
    }
  }

  const breakdown = completion.breakdown || [];
  const total = Number(completion.total_count) || 0;
  const getCount = (status) => {
    const item = breakdown.find((row) => getBreakdownStatus(row) === status);
    return Number(item && item.count) || 0;
  };
  const acknowledgedCount = getCount('acknowledged') || Number(completion.completed_count) || 0;
  const seenCount = getCount('seen');
  const acknowledgedWidth = total ? Math.min(100, (acknowledgedCount / total) * 100) : Number(completion.percentage) || 0;
  const seenWidth = total ? Math.min(100 - acknowledgedWidth, (seenCount / total) * 100) : 0;

  const employeeActions = (() => {
    return [
      ...(document.require_acknowledgment ? [t('Acknowledgement')] : []),
      ...(document.require_signature ? [t('Signature')] : []),
      ...(!isCompanyOwner ? [t('Upload')] : [])
    ]
  })()

  const handleRemindOutstanding = async () => {
    const res = await sendDocumentOutstandingReminder(companyId, document.id)
    console.log(res)
    notify.show(t('Reminder sent successfully'))
  }

  return (
    <div className={styles.container}>
      <div className={styles.viewer}>
        <FileViewer files={files} loading={loadingFiles} />
      </div>
      <div className={styles.details}>
        <div className={styles.completion}>
          <div className={styles.title}>{t('Audience')}</div>
          <div className={styles.percentage}>{renderAudienceLabel(document)}</div>
          <div className={styles.summary}>
            {t('{{count}} employees', {count: completion.total_count})}
          </div>
        </div>
        <div className={styles.completion}>
          <div className={styles.title}>{t('completion')}</div>
          <div className={styles.percentage}>
            {completion.percentage || 0}
            %
          </div>
          <div className={styles.summary}>
            {t('{{completed}} of {{total}} acknowledged', {
              completed: completion.completed_count || 0,
              total: completion.total_count || 0,
            })}
          </div>
          <div className={styles.progress}>
            <div
              className={`${styles.progressSegment} ${styles.acknowledgedSegment}`}
              style={{ width: `${acknowledgedWidth}%` }}
            />
            <div
              className={`${styles.progressSegment} ${styles.seenSegment}`}
              style={{ width: `${seenWidth}%` }}
            />
            <div
              className={`${styles.progressSegment} ${styles.remainingSegment}`}
              style={{ width: `${Math.max(0, 100 - acknowledgedWidth - seenWidth)}%` }}
            />
          </div>
          <div className={styles.breakdown}>
            {breakdown.map((item, index) => {
              const status = getBreakdownStatus(item)
              return (
                <div
                  key={item.key || item.status || item.label || index}
                  className={`${styles.breakdownItem} ${status === 'overdue' ? styles.overdueItem : ''}`}
                >
                  <span className={`${styles.statusDot} ${styles[`${status}Dot`] || ''}`} />
                  <span className={styles.statusLabel}>{t(item.label)}</span>
                  <span className={styles.statusCount}>{item.count}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <button type="button" className={styles.remindButton} onClick={handleRemindOutstanding}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={16}
              height={16}
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              aria-hidden="true"
              viewBox="0 0 24 24"
            >
              <path d="M10.268 21a2 2 0 0 0 3.464 0M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
            </svg>
            { t('Remind outstanding') }
          </button>
        </div>
        <div className={styles.grid}>
          <div>
            <div className={styles.title}>{t('Type')}</div>
            <div className={styles.value}>{document.document_type.name}</div>
            <div className={styles.summary}>{document.category.name}</div>
          </div>
          <div>
            <div className={styles.title}>{t('Effective')}</div>
            <div className={styles.value}>{formatDate(document.effective_date)}</div>
          </div>
          <div>
            <div className={styles.title}>{t('Expires')}</div>
            <div className={styles.value}>{formatDate(document.expiration_date)}</div>
          </div>
          <div>
            <div className={styles.title}>{t('Due Date')}</div>
            <div className={styles.value}>{formatDate(document.due_date_for_action)}</div>
          </div>
          <div>
            <div className={styles.title}>{t('Uploaded by')}</div>
            <div className={styles.value}>{isCompanyOwner ? document.creator.name : '-'}</div>
          </div>
          <div>
            <div className={styles.title}>{t('Empoyee action')}</div>
            <div className={styles.value}>{employeeActions.join(', ')}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Overview
